/**
 * Event Filter Matcher Utility
 *
 * Utility functions for matching incoming WebSocket events against
 * the current filter state from useEventsState().
 *
 * Used by the WebSocket context to determine if an incoming event
 * should be displayed immediately or increment the "new events available" counter.
 */

import type {
	Event,
	EventCreatedEvent,
	EventSensor,
	EventUpdatedEvent,
	Installation,
	Zone,
} from "@sitescene/types";

import { eventSeverityToNumber } from "@sitescene/shared";
import { EventSeverity, type EventType } from "@sitescene/types";

import type { EventsState } from "@/context/events";
import type { LocationZoneSensorIdSets } from "@/features/events/utils/filter-valid-ids";

import { isAllFilterIdsSelected } from "@/features/events/utils/filter-valid-ids";
import { getRefId } from "@/features/locations/utils/installation-ids";
import { buildDateTimeRangeTimestamps } from "@/utils/date-time";

/**
 * Event detail from WebSocket event (either created or updated).
 * Enriched broadcasts may also carry populated installation / zones / sensors.
 */
export type WebSocketEventDetail = (EventCreatedEvent | EventUpdatedEvent) & {
	installation?: Installation | string;
	sensors?: Array<EventSensor | string>;
	zones?: Array<string | Zone>;
};

/**
 * Check if an incoming WebSocket event matches all current filters
 *
 * @param eventDetail - The event detail from the WebSocket event
 * @param filters - Current filter state from useEventsState()
 * @param timeZone - Location-derived IANA timezone used to anchor date+time
 *   bounds to the site wall clock. When absent, the browser local timezone is
 *   used (same continuous datetime span as the events query, incl. end-minute
 *   buffer).
 * @param precomputedRange - Optional prebuilt datetime span for hot loops
 * @param idSets - Org-tree ID lists; used to treat "all selected" L/Z/S as inactive
 * @returns true if the event matches all active filters
 */
export function doesEventMatchFilters(
	eventDetail: WebSocketEventDetail,
	filters: EventsState,
	timeZone?: null | string,
	precomputedRange?: null | { endTimestamp: string; startTimestamp: string },
	idSets?: LocationZoneSensorIdSets,
): boolean {
	// Check each filter - all must pass for the event to match

	// 1. Severity filter
	if (!matchesSeverity(eventDetail, filters.severityFilter)) {
		return false;
	}

	// 2. Search query filter (matches title or description)
	if (!matchesSearch(eventDetail, filters.searchQueryFilter)) {
		return false;
	}

	// 3. Date/datetime range → single continuous span (location timezone or browser-local).
	// `precomputedRange` lets hot loops build the span once instead of per event;
	// `undefined` means "not provided → build it" (a genuine `null` = no date filter).
	const dateTimeRange =
		precomputedRange !== undefined
			? precomputedRange
			: buildDateTimeRangeTimestamps(filters.dateRangeFilter, timeZone);
	if (dateTimeRange && !matchesTimestampRange(eventDetail, dateTimeRange)) {
		return false;
	}

	// 4. Event types filter
	if (!matchesEventTypes(eventDetail, filters.eventTypesFilter)) {
		return false;
	}

	// 5–7. Location / zone / sensor filters (enriched WS broadcasts carry these).
	// Mirror the events API: each non-empty dimension must pass (AND across
	// dimensions). "All selected" (full org ID list) is treated as inactive.
	if (
		!matchesLocationFilter(
			eventDetail,
			filters.locationsFilter,
			idSets?.locationIds,
		)
	) {
		return false;
	}

	if (!matchesZoneFilter(eventDetail, filters.zonesFilter, idSets?.zoneIds)) {
		return false;
	}

	if (
		!matchesSensorFilter(eventDetail, filters.sensorsFilter, idSets?.sensorIds)
	) {
		return false;
	}

	return true;
}

/**
 * Check if an Event object (from React Query cache) matches all current filters
 *
 * This is used to filter events from the cache when filters change,
 * removing events that no longer match the new filter criteria.
 *
 * @param event - The Event object from the cache
 * @param filters - Current filter state from useEventsState()
 * @returns true if the event matches all active filters
 */
export function doesEventMatchFiltersFromCache(
	event: Event,
	filters: EventsState,
	timeZone?: null | string,
	precomputedRange?: null | { endTimestamp: string; startTimestamp: string },
	idSets?: LocationZoneSensorIdSets,
): boolean {
	// Convert Event to a format compatible with filter matchers
	// Event has startTimestamp as Date, but it might be serialized as string in cache
	// Handle both cases: Date object or ISO string
	let startTimestamp: string;
	if (event.startTimestamp instanceof Date) {
		startTimestamp = event.startTimestamp.toISOString();
	} else if (typeof event.startTimestamp === "string") {
		startTimestamp = event.startTimestamp;
	} else if (event.startTimestamp != null) {
		// Handle other cases (number timestamp, etc.)
		startTimestamp = new Date(event.startTimestamp).toISOString();
	} else {
		// Fallback: use current time if startTimestamp is null/undefined
		// This shouldn't happen in practice, but provides a safety net
		startTimestamp = new Date().toISOString();
	}

	const eventDetail = {
		description: event.description,
		installation: event.installation,
		sensors: event.sensors,
		severity: event.severity,
		startTimestamp,
		title: event.title,
		types: event.types,
		zones: event.zones,
	} as WebSocketEventDetail;

	return doesEventMatchFilters(
		eventDetail,
		filters,
		timeZone,
		precomputedRange,
		idSets,
	);
}

/** Location UUID from an enriched installation, if present. */
export function getEventLocationId(
	eventDetail: WebSocketEventDetail,
): string | undefined {
	const { installation } = eventDetail;
	if (!installation || typeof installation === "string") return undefined;
	return getRefId(installation.location);
}

/** Sensor UUIDs from enriched (or string-ref) sensors. */
export function getEventSensorIds(eventDetail: WebSocketEventDetail): string[] {
	return (eventDetail.sensors ?? [])
		.map((sensor) => (typeof sensor === "string" ? sensor : sensor.id))
		.filter((id): id is string => Boolean(id));
}

/** Zone UUIDs from enriched (or string-ref) zones. */
export function getEventZoneIds(eventDetail: WebSocketEventDetail): string[] {
	return (eventDetail.zones ?? [])
		.map((zone) => (typeof zone === "string" ? zone : zone.id))
		.filter((id): id is string => Boolean(id));
}

/**
 * Check if event types match the event types filter
 *
 * @param eventDetail - The event detail with types
 * @param eventTypesFilter - Array of event types to filter by
 * @returns true if event has a matching type or filter is empty
 */
export function matchesEventTypes(
	eventDetail: WebSocketEventDetail,
	eventTypesFilter: EventType[],
): boolean {
	if (eventTypesFilter.length === 0) {
		return true;
	}

	const types = eventDetail.types ?? [];
	if (types.length === 0) {
		return false;
	}

	return types.some((type) => eventTypesFilter.includes(type));
}

/**
 * Location filter: event's location must be in the selected set.
 * Fail closed when the filter is active but enrichment did not include location.
 */
export function matchesLocationFilter(
	eventDetail: WebSocketEventDetail,
	locationsFilter: string[],
	allLocationIds?: readonly string[],
): boolean {
	if (!isLocationZoneSensorDimensionActive(locationsFilter, allLocationIds)) {
		return true;
	}

	const locationId = getEventLocationId(eventDetail);
	if (!locationId) return false;
	return locationsFilter.includes(locationId);
}

/**
 * Check if search query matches event title or description
 *
 * @param eventDetail - The event detail with title and description
 * @param searchQuery - The search query string
 * @returns true if search matches or query is empty
 */
export function matchesSearch(
	eventDetail: WebSocketEventDetail,
	searchQuery: string,
): boolean {
	// Empty search matches everything
	const trimmedQuery = searchQuery.trim().toLowerCase();
	if (trimmedQuery === "") {
		return true;
	}

	// Check title and description for the search query
	const title = eventDetail.title.toLowerCase();
	const description = eventDetail.description.toLowerCase();

	return title.includes(trimmedQuery) || description.includes(trimmedQuery);
}

/**
 * Sensor filter: any event sensor may match (OR within dimension).
 * Fail closed when the filter is active but the event has no sensor ids.
 */
export function matchesSensorFilter(
	eventDetail: WebSocketEventDetail,
	sensorsFilter: string[],
	allSensorIds?: readonly string[],
): boolean {
	if (!isLocationZoneSensorDimensionActive(sensorsFilter, allSensorIds)) {
		return true;
	}

	const sensorIds = getEventSensorIds(eventDetail);
	if (sensorIds.length === 0) return false;
	const allowed = new Set(sensorsFilter);
	return sensorIds.some((id) => allowed.has(id));
}

/**
 * Check if event severity meets or exceeds the filter threshold
 *
 * Severity mapping:
 * - CRITICAL: severity >= 9
 * - HIGH: severity >= 7
 * - MODERATE: severity >= 5
 * - LOW: severity >= 1 (shows all)
 * - null: shows all
 *
 * @param eventDetail - The event detail with severity
 * @param severityFilter - The severity filter or null for all
 * @returns true if event severity meets threshold
 */
export function matchesSeverity(
	eventDetail: WebSocketEventDetail,
	severityFilter: EventSeverity | null,
): boolean {
	// Null filter means show all severities
	if (severityFilter === null) {
		return true;
	}

	// Convert the enum to numeric threshold
	const threshold = eventSeverityToNumber(severityFilter);

	// Event severity must meet or exceed the threshold
	return eventDetail.severity >= threshold;
}

/**
 * Check if the event's startTimestamp falls within an absolute [start, end]
 * datetime span (inclusive). Bounds are ISO instants produced by
 * `buildDateTimeRangeTimestamps`, so client and API agree exactly.
 */
export function matchesTimestampRange(
	eventDetail: WebSocketEventDetail,
	{
		endTimestamp,
		startTimestamp,
	}: { endTimestamp: string; startTimestamp: string },
): boolean {
	const eventTime = new Date(eventDetail.startTimestamp).getTime();
	return (
		eventTime >= new Date(startTimestamp).getTime() &&
		eventTime <= new Date(endTimestamp).getTime()
	);
}

/**
 * Zone filter: any event zone may match (OR within dimension).
 * Fail closed when the filter is active but the event has no zone ids.
 */
export function matchesZoneFilter(
	eventDetail: WebSocketEventDetail,
	zonesFilter: string[],
	allZoneIds?: readonly string[],
): boolean {
	if (!isLocationZoneSensorDimensionActive(zonesFilter, allZoneIds)) {
		return true;
	}

	const zoneIds = getEventZoneIds(eventDetail);
	if (zoneIds.length === 0) return false;
	const allowed = new Set(zonesFilter);
	return zoneIds.some((id) => allowed.has(id));
}

/** True when this L/Z/S filter dimension should constrain matching. */
function isLocationZoneSensorDimensionActive(
	filterIds: string[],
	allIds: readonly string[] | undefined,
): boolean {
	if (filterIds.length === 0) return false;
	if (allIds && isAllFilterIdsSelected(filterIds, allIds)) return false;
	return true;
}
